/**
 * Route-to-Header Mapping (Single Source of Truth)
 * 
 * This file defines the metadata for all case page headers.
 * Headers derive from route segments, NOT from page components or nav config.
 * This guarantees consistency across the application.
 */

export interface CasePageMeta {
  title: string;
  subtitle?: string;
}

export const casePageMeta: Record<string, CasePageMeta> = {
  // Index route (case overview)
  '': {
    title: 'Case Overview',
  },
  overview: {
    title: 'Case Overview',
  },
  
  // Core case management phases
  journal: {
    title: 'Journal',
    subtitle: 'Document what happened, as it happened.',
  },
  chronology: {
    title: 'Case History',
    subtitle: 'Everything, automatically ordered for discovery and review.',
  },
  activity: {
    title: 'Activity',
    subtitle: 'A record of all linking actions in this case',
  },
  suggestions: {
    title: 'AI Suggestions',
    subtitle: 'AI suggestions are optional. Review before accepting.',
  },
  exhibits: {
    title: 'Exhibits',
    subtitle: 'Your organized evidence — labeled, numbered, and ready for court.',
  },
  evidence: {
    title: 'Evidence',
    subtitle: 'Files and exhibits',
  },
  timeline: {
    title: 'Timeline',
    subtitle: 'Anchor events, data, and evidence to specific points in time.',
  },
  filings: {
    title: 'Filings',
    subtitle: 'Prepare and submit what goes to court.',
  },
  drafts: {
    title: 'Document Center',
    subtitle: 'Write and refine what you are saying.',
  },
  documents: {
    title: 'Documents',
    subtitle: 'Manage drafts, forms, and filings in one place',
  },
  build: {
    title: 'Build Your Case',
    subtitle: 'Record events, organize evidence, and build your narrative timeline.',
  },
  discovery: {
    title: 'Discovery',
    subtitle: 'Discovery requests and responses',
  },
  members: {
    title: 'Case Members',
    subtitle: 'Manage team access and permissions',
  },
  settings: {
    title: 'Case Settings',
    subtitle: 'Configure case preferences',
  },
  communications: {
    title: 'Communications',
    subtitle: 'Messages and notifications',
  },
  
  // Additional phases
  calendar: {
    title: 'Calendar',
    subtitle: 'Tasks and Deadlines',
  },
  forms: {
    title: 'Forms',
    subtitle: 'Complete structured court documents.',
  },
  insights: {
    title: 'Insights',
    subtitle: 'AI-powered analysis',
  },
  inbox: {
    title: 'Inbox',
    subtitle: 'Incoming items',
  },
  notifications: {
    title: 'Notifications',
    subtitle: 'Case updates',
  },
  share: {
    title: 'Share',
    subtitle: 'Collaborate with others',
  },
  exports: {
    title: 'Exports',
    subtitle: 'Generate documents and reports to bring to court, share with your attorney, or respond to a discovery request.',
  },
  toolkits: {
    title: 'Toolkits',
    subtitle: 'Specialized tools',
  },
  scan: {
    title: 'Scan',
    subtitle: 'Document scanning',
  },
  attachments: {
    title: 'Attachments',
    subtitle: 'Case files',
  },
  
  // Sub-routes
  'filings/builder': {
    title: 'Filing Builder',
    subtitle: 'Create court document',
  },
  'filings/preview': {
    title: 'Filing Preview',
    subtitle: 'Review before submission',
  },
  'forms/fill': {
    title: 'Fill Form',
  },
  guides: {
    title: 'Case Guides',
    subtitle: 'Step-by-step court form instructions',
  },
  'guides/list': {
    title: 'Legal Guides',
  },
  'guides/viewer': {
    title: 'Guide Viewer',
  },
  messages: {
    title: 'Messages',
    subtitle: 'Case communications',
  },
  'imports/wizard': {
    title: 'Import Wizard',
    subtitle: 'Bulk case import',
  },
  'reports/activity-log': {
    title: 'Activity Log',
    subtitle: 'A record of actions taken in this case',
  },
  'key-details': {
    title: 'SmartBox™',
    subtitle: 'What ThreadLock knows about your case right now, derived from files you’ve shared with SmartBox™.',
  },
};
